import React from 'react'
import PropTypes from 'prop-types'
import { IconButton, makeStyles } from '@material-ui/core'
import dayjs from 'dayjs'
import clsx from 'clsx'
import { ICON_NAMES } from '../../../../constants'
import Icon from '../../../atoms/Icon'
import Tooltip from '../../../atoms/Tooltip'
import { useBoolean } from '../../../../hooks'
import Menu from '../../../molecules/Menu'
import FastAvatar from '../../../molecules/FastAvatar'

const useStyles = makeStyles(() => ({
  container: {
    padding: '1rem 1.5rem',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
    borderRadius: 'var(--border-radius-sm)',
    position: 'relative',
    '&:hover': {
      background: 'var(--color-surface-050)',
      borderRadius: 'var(--border-radius-sm)'
    }
  },
  content: {
    width: 'calc(100% - 4rem)'
  },
  icon: {
    paddingRight: '1rem'
  },
  name: {
    fontSize: '1rem',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden'
  },
  time: {
    fontSize: '0.75rem',
    color: 'var(--text-color-secondary)',
    marginRight: '.5rem'
  },
  footer: {
    display: 'flex',
    flexDirection: 'row'
  },
  owner: {
    display: 'flex',
    marginLeft: 'auto'
  },
  menu: {
    position: 'absolute',
    right: '0.5rem'
  },
  actionsMenu: {
    backgroundColor: 'var(--color-surface-050)'
  },
  columnedContainer: {
    display: 'grid',
    alignItems: 'center',
    gap: '1rem',
    padding: '0.75rem 1rem',
    cursor: 'pointer',
    borderRadius: 'var(--border-radius-sm)',
    position: 'relative',
    '&:hover': {
      background: 'var(--color-surface-050)'
    }
  },
  columnedNameCell: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 0
  },
  columnedNameText: {
    fontSize: '0.875rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden'
  },
  columnedDateCell: {
    fontSize: '0.875rem',
    color: 'var(--text-color-subtle)',
    whiteSpace: 'nowrap'
  },
  columnedUploaderCell: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '0.5rem',
    minWidth: 0,
    fontSize: '0.875rem',
    color: 'var(--text-color-main)'
  },
  columnedUploaderName: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden'
  },
  columnedUploaderUnknown: {
    color: 'var(--text-color-secondary)'
  },
  columnedActionsCell: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '0.25rem'
  },
  inlineActionButton: {
    padding: '0.25rem'
  }
}))

const renderInlineActions = (inlineActions, classes, componentStyles) => {
  if (!inlineActions || inlineActions.length === 0) return null

  return inlineActions.map((action, idx) => {
    const button = (
      <IconButton
        key={`inline-action-${action.iconName}-${idx}`}
        className={classes.inlineActionButton}
        style={componentStyles?.inlineActionButton}
        onClick={
          action.onClick
            ? (e) => {
              e.stopPropagation()
              action.onClick(e)
            }
            : undefined
        }
      >
        <Icon
          customSize={componentStyles?.inlineActionIconSize || '1.1rem'}
          name={action.iconName}
        />
      </IconButton>
    )

    return action.tooltip ? (
      <Tooltip
        key={`inline-action-${action.iconName}-${idx}`}
        title={action.tooltip}
        placement='top'
      >
        {button}
      </Tooltip>
    ) : (
      button
    )
  })
}

const DocumentItem = ({
  document,
  options,
  onClick,
  variant,
  dateFormat,
  showAvatar,
  inlineActions,
  actionsAlwaysVisible,
  gridColumns,
  componentStyles
}) => {
  const classes = useStyles()
  const [hover, toggleHover] = useBoolean()

  const formattedDate = document?.updatedDate
    ? dayjs(document.updatedDate).format(dateFormat)
    : ''

  const uploaderFirst = document?.createdByUser?.firstName || ''
  const uploaderLast = document?.createdByUser?.lastName || ''
  const uploaderName = `${uploaderFirst} ${uploaderLast}`.trim()
  const uploaderInitials = [uploaderFirst, uploaderLast]
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase())
    .join('')
  const hasUploaderName = uploaderName.length > 0

  const showActions = actionsAlwaysVisible || hover

  if (variant === 'columned') {
    return (
      <Tooltip title={document?.name || ''} placement='top'>
        <div
          className={classes.columnedContainer}
          style={{
            gridTemplateColumns: gridColumns,
            ...componentStyles?.container
          }}
          onMouseEnter={toggleHover.on}
          onMouseLeave={toggleHover.off}
          onClick={onClick}
        >
          <div className={classes.columnedNameCell} style={componentStyles?.nameCell}>
            <div className={classes.icon} style={componentStyles?.iconWrap}>
              <Icon
                name={ICON_NAMES.document}
                customSize={componentStyles?.documentIconSize || '1.5rem'}
              />
            </div>
            <div className={classes.columnedNameText} style={componentStyles?.nameText}>
              {document?.name}
            </div>
          </div>

          <div className={classes.columnedDateCell} style={componentStyles?.dateCell}>
            {formattedDate}
          </div>

          <div className={classes.columnedUploaderCell} style={componentStyles?.uploaderCell}>
            {showAvatar && (
              <FastAvatar
                size='sm'
                avatarUrl={document?.createdByUser?.avatarUrl}
                abbreviation={uploaderInitials || null}
              />
            )}
            <span
              className={clsx(
                classes.columnedUploaderName,
                !hasUploaderName && classes.columnedUploaderUnknown
              )}
              style={componentStyles?.uploaderName}
            >
              {hasUploaderName ? uploaderName : 'Unknown'}
            </span>
          </div>

          <div className={classes.columnedActionsCell} style={componentStyles?.actionsCell}>
            {showActions && renderInlineActions(inlineActions, classes, componentStyles)}
            {showActions && (
              <Menu options={options}>
                {({ setAnchorEl }) => {
                  return (
                    <IconButton
                      className={clsx(classes.inlineActionButton, classes.actionsMenu)}
                      style={{
                        ...componentStyles?.inlineActionButton,
                        ...componentStyles?.menuButton
                      }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setAnchorEl(e)
                      }}
                    >
                      <Icon
                        customSize={componentStyles?.menuIconSize || '1.1rem'}
                        name={ICON_NAMES.vertDots}
                      />
                    </IconButton>
                  )
                }}
              </Menu>
            )}
          </div>
        </div>
      </Tooltip>
    )
  }

  return (
    <Tooltip title={document.name} placement='top'>
      <div
        className={classes.container}
        style={componentStyles?.container}
        onMouseEnter={toggleHover.on}
        onMouseLeave={toggleHover.off}
        onClick={onClick}
      >
        <div className={classes.icon} style={componentStyles?.iconWrap}>
          <Icon
            name={ICON_NAMES.document}
            customSize={componentStyles?.documentIconSize || '1.5rem'}
          />
        </div>
        <div className={classes.content} style={componentStyles?.content}>
          <div className={classes.name} style={componentStyles?.nameText}>{document?.name}</div>
          <div className={classes.footer} style={componentStyles?.footer}>
            <div className={classes.time} style={componentStyles?.dateCell}>{formattedDate}</div>
            <div className={classes.createdByUser} style={componentStyles?.uploaderName}>
              {`${document?.createdByUser?.firstName || ''} ${
                document?.createdByUser?.lastName || ''
              }`}
            </div>
          </div>
        </div>
        {hover && (
          <div className={classes.menu} style={componentStyles?.actionsCell}>
            <Menu options={options}>
              {({ setAnchorEl }) => {
                return (
                  <IconButton
                    className={classes.actionsMenu}
                    style={componentStyles?.menuButton}
                    onClick={setAnchorEl}
                  >
                    <Icon
                      customSize={componentStyles?.menuIconSize || '1.2rem'}
                      name={ICON_NAMES.vertDots}
                    />
                  </IconButton>
                )
              }}
            </Menu>
          </div>
        )}
      </div>
    </Tooltip>
  )
}

DocumentItem.propTypes = {
  document: PropTypes.object,
  onClick: PropTypes.func,
  options: PropTypes.array,
  variant: PropTypes.oneOf(['compact', 'columned']),
  dateFormat: PropTypes.string,
  showAvatar: PropTypes.bool,
  inlineActions: PropTypes.arrayOf(
    PropTypes.shape({
      iconName: PropTypes.string.isRequired,
      tooltip: PropTypes.string,
      onClick: PropTypes.func
    })
  ),
  actionsAlwaysVisible: PropTypes.bool,
  gridColumns: PropTypes.string,
  componentStyles: PropTypes.shape({
    container: PropTypes.object,
    content: PropTypes.object,
    footer: PropTypes.object,
    nameCell: PropTypes.object,
    iconWrap: PropTypes.object,
    nameText: PropTypes.object,
    dateCell: PropTypes.object,
    uploaderCell: PropTypes.object,
    uploaderName: PropTypes.object,
    actionsCell: PropTypes.object,
    inlineActionButton: PropTypes.object,
    menuButton: PropTypes.object,
    documentIconSize: PropTypes.string,
    inlineActionIconSize: PropTypes.string,
    menuIconSize: PropTypes.string
  })
}

DocumentItem.defaultProps = {
  variant: 'compact',
  dateFormat: 'MM/DD @ h:mm a',
  showAvatar: false,
  inlineActions: [],
  actionsAlwaysVisible: false,
  gridColumns: undefined,
  componentStyles: undefined
}

export default DocumentItem
